import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {PostTypeEnum} from '../enums/post-type.enum';
import {CurrentUserViewModel} from '../models/current-user.view-model';
import {ResponseNotificationService} from './response-notification.service';
import {PostPublishOrchestratorService} from './post-publish-orchestrator.service';
import {ProjectViewModel} from '../../features/user/garage/project.view-model';
import {EventApiModel} from '../../api/models/event.api.model';
import {TimelineComponent} from '../../shared/timeline/timeline.component';
import {PostViewModel} from '../../shared/timeline/post.view-model';

export interface MediaPostPublishParams {
  file: File;
  caption: string;
  isVideo: boolean;
  projectVM?: ProjectViewModel | null;
  eventAM?: EventApiModel | null;
  currentUser?: CurrentUserViewModel | null;
  timeline?: TimelineComponent | null;
  thumbnailFile?: File | null;
  thumbnailTime?: number | null;
  filterPreset?: string | null;
  localThumbnailUri?: string | null;
}

export interface NativeMediaPostPublishParams extends Omit<MediaPostPublishParams, 'file' | 'isVideo' | 'thumbnailFile' | 'thumbnailTime' | 'filterPreset'> {}

/**
 * Keeps publishing ownership in Angular even when a native Flutter surface
 * collected the media. Both composers therefore share optimistic UI, retries,
 * preprocessing and the background upload queue.
 */
@Injectable({providedIn: 'root'})
export class MediaPostPublishService {
  private static readonly maxVideoSizeBytes = 500 * 1024 * 1024;
  private static readonly maxImageSizeBytes = 50 * 1024 * 1024;
  private static readonly maxVideoDurationSeconds = 60;
  private static optimisticIdSequence = 0;

  constructor(
    private readonly postPublisher: PostPublishOrchestratorService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
  ) {}

  public async publish(params: MediaPostPublishParams): Promise<PostViewModel | null> {
    if (!this.isValidSize(params.file, params.isVideo) || !(await this.isValidDuration(params.file, params.isVideo))) {
      return null;
    }

    const optimistic = this.createOptimisticPost(params);
    params.timeline?.insertOptimisticPostIntoCollection(optimistic, optimistic.localThumbnailUri || null);
    this.notifications.emitMessage(this.translate.instant('shared.modal.component.add_media.info.processing_in_background'));

    window.setTimeout(() => {
      this.postPublisher.queueMediaPost({
        file: params.file,
        caption: params.caption || '',
        thumbnailFile: params.thumbnailFile,
        thumbnailTime: params.thumbnailTime,
        filterPreset: params.filterPreset,
        isVideo: params.isVideo,
        projectIri: params.projectVM?.['@id'] || null,
        eventIri: params.eventAM?.['@id'] || null,
        onSuccess: (post) => params.timeline?.replaceOptimisticPostInCollection(optimistic, post),
        onError: (error) => {
          console.error('[MediaPostPublish] background publish failed', error);
          params.timeline?.removeOptimisticPostFromCollection(optimistic);
          this.notifications.emitError(this.translate.instant('shared.modal.component.add_media.error.upload_failed'));
        },
      });
    }, 0);

    return optimistic;
  }

  /**
   * Native Flutter media is uploaded from disk by the platform plugin. This
   * branch deliberately never receives a File/Blob so the WebView cannot
   * duplicate a 60-second video in memory.
   */
  public beginNativePublish(params: NativeMediaPostPublishParams): PostViewModel {
    const optimistic = this.createOptimisticPost({...params, file: null as unknown as File, isVideo: false});
    params.timeline?.insertOptimisticPostIntoCollection(optimistic, optimistic.localThumbnailUri || null);
    this.notifications.emitMessage(this.translate.instant('shared.modal.component.add_media.info.processing_in_background'));
    return optimistic;
  }

  public completeNativePublish(optimistic: PostViewModel, mediaIri: string, params: NativeMediaPostPublishParams): void {
    this.postPublisher.createPostForUploadedMedia({
      mediaIri,
      caption: params.caption || '',
      projectIri: params.projectVM?.['@id'] || null,
      eventIri: params.eventAM?.['@id'] || null,
    }).subscribe({
      next: (post) => params.timeline?.replaceOptimisticPostInCollection(optimistic, post),
      error: (error) => {
        console.error('[MediaPostPublish] native post creation failed', error);
        params.timeline?.removeOptimisticPostFromCollection(optimistic);
        this.notifications.emitError(this.translate.instant('shared.modal.component.add_media.error.upload_failed'));
      },
    });
  }

  public failNativePublish(optimistic: PostViewModel, params: NativeMediaPostPublishParams): void {
    params.timeline?.removeOptimisticPostFromCollection(optimistic);
    this.notifications.emitError(this.translate.instant('shared.modal.component.add_media.error.upload_failed'));
  }

  private isValidSize(file: File, isVideo: boolean): boolean {
    const max = isVideo ? MediaPostPublishService.maxVideoSizeBytes : MediaPostPublishService.maxImageSizeBytes;
    if (file.size <= max) return true;
    this.notifications.emitError(this.translate.instant(
      isVideo ? 'shared.modal.component.add_media.error.video_too_large' : 'shared.modal.component.add_media.error.image_too_large'
    ));
    return false;
  }

  private async isValidDuration(file: File, isVideo: boolean): Promise<boolean> {
    if (!isVideo) return true;
    const duration = await this.readVideoDuration(file);
    if (duration <= MediaPostPublishService.maxVideoDurationSeconds + 0.01) return true;
    this.notifications.emitError(this.translate.instant('shared.modal.component.add_media.error.video_too_long'));
    return false;
  }

  private readVideoDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const source = URL.createObjectURL(file);
      const finish = (value: number) => {
        video.onloadedmetadata = null;
        video.onerror = null;
        URL.revokeObjectURL(source);
        resolve(value);
      };
      video.preload = 'metadata';
      video.onloadedmetadata = () => finish(Number(video.duration) || 0);
      video.onerror = () => finish(0);
      video.src = source;
    });
  }

  private createOptimisticPost(params: MediaPostPublishParams): PostViewModel {
    const postType = params.projectVM ? PostTypeEnum.PROJECT_MEDIA_POST : PostTypeEnum.EVENT_MEDIA_POST;
    const optimistic = PostViewModel.create(params.caption || '', postType, [], params.projectVM || null, params.eventAM || null);
    MediaPostPublishService.optimisticIdSequence = (MediaPostPublishService.optimisticIdSequence + 1) % 100000;
    optimistic.id = -((Date.now() % 1000000000) + MediaPostPublishService.optimisticIdSequence);
    optimistic['@id'] = `optimistic-${Math.abs(optimistic.id)}`;
    optimistic.postedBy = params.currentUser || null;
    optimistic.isPlaceholder = true;
    optimistic.processingStatus = 'processing';
    optimistic.createdAt = new Date();
    optimistic.updatedAt = optimistic.createdAt;
    optimistic.localThumbnailUri = params.localThumbnailUri || null;
    return optimistic;
  }
}
